import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import 'xterm/css/xterm.css';
import Sidebar from './Sidebar';
import { api } from './api';
import { Server } from './types';

interface SSHConfig {
    id?: number;
    host_override: string;
    port: number;
    username: string;
    auth_method: 'password' | 'key';
    has_password: boolean;
    has_key: boolean;
}

interface SSHFormData {
    host_override: string;
    port: number;
    username: string;
    auth_method: 'password' | 'key';
    password: string;
    private_key: string;
    passphrase: string;
}

export default function TerminalPage() {
    const { id }   = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [server, setServer]         = useState<Server | null>(null);
    const [sshConfig, setSshConfig]   = useState<SSHConfig | null | 'loading'>('loading');
    const [showForm, setShowForm]     = useState(false);
    const [formData, setFormData]     = useState<SSHFormData>({
        host_override: '', port: 22, username: 'root',
        auth_method: 'password', password: '', private_key: '', passphrase: '',
    });
    const [saving, setSaving]         = useState(false);
    const [formError, setFormError]   = useState('');
    const [connStatus, setConnStatus] = useState<'idle' | 'connecting' | 'connected' | 'error'>('idle');
    const [statusMsg, setStatusMsg]   = useState('');

    const termRef      = useRef<Terminal | null>(null);
    const fitRef       = useRef<FitAddon | null>(null);
    const wsRef        = useRef<WebSocket | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    /* ── Загрузка данных ── */
    useEffect(() => {
        if (!id) return;
        Promise.all([
            api.get<Server>(`/servers/${id}`).then(setServer),
            api.get<SSHConfig | null>(`/servers/${id}/ssh`).then(cfg => setSshConfig(cfg)),
        ]).catch(() => setSshConfig(null));
    }, [id]);

    /* ── Инициализация терминала ── */
    useEffect(() => {
        if (!containerRef.current) return;

        const term = new Terminal({
            theme: {
                background: '#0a0e1a',
                foreground: '#e2e8f0',
                cursor:     '#6366f1',
                selectionBackground: 'rgba(99,102,241,0.3)',
                black:   '#1e2337', red:     '#ef4444',
                green:   '#22c55e', yellow:  '#f59e0b',
                blue:    '#6366f1', magenta: '#a78bfa',
                cyan:    '#22d3ee', white:   '#e2e8f0',
            },
            fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
            fontSize: 14,
            lineHeight: 1.4,
            cursorBlink: true,
            scrollback: 5000,
        });

        const fit = new FitAddon();
        term.loadAddon(fit);
        term.open(containerRef.current);
        fit.fit();

        termRef.current = term;
        fitRef.current  = fit;

        const onResize = () => {
            fit.fit();
            if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({
                    type: 'resize',
                    cols: term.cols,
                    rows: term.rows,
                }));
            }
        };
        window.addEventListener('resize', onResize);

        return () => {
            window.removeEventListener('resize', onResize);
            term.dispose();
            wsRef.current?.close();
        };
    }, []);

    /* ── Подключение SSH ── */
    const connect = useCallback(() => {
        const term = termRef.current;
        if (!term || !id) return;

        term.clear();
        term.writeln('\x1b[36mПодключение...\x1b[0m');
        setConnStatus('connecting');
        setStatusMsg('');

        const token  = localStorage.getItem('token') ?? '';
        const proto  = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const wsUrl  = `${proto}//${window.location.host}/api/ws/terminal/${id}?token=${token}`;
        const ws     = new WebSocket(wsUrl);
        ws.binaryType = 'arraybuffer';
        wsRef.current = ws;

        const disposeInput = term.onData(data => {
            if (ws.readyState === WebSocket.OPEN) {
                ws.send(new TextEncoder().encode(data));
            }
        });

        ws.onopen = () => {
            ws.send(JSON.stringify({ type: 'resize', cols: term.cols, rows: term.rows }));
        };

        ws.onmessage = (e) => {
            if (e.data instanceof ArrayBuffer) {
                term.write(new Uint8Array(e.data));
            } else if (typeof e.data === 'string') {
                try {
                    const msg = JSON.parse(e.data);
                    if (msg.type === 'connected') {
                        setConnStatus('connected');
                        term.writeln('\x1b[32mПодключено\x1b[0m');
                        fitRef.current?.fit();
                    } else if (msg.type === 'error') {
                        setConnStatus('error');
                        setStatusMsg(msg.message ?? 'Ошибка');
                        term.writeln('\x1b[31m' + (msg.message ?? 'Ошибка') + '\x1b[0m');
                    }
                } catch {
                    term.write(e.data);
                }
            }
        };

        ws.onerror = () => {
            setConnStatus('error');
            setStatusMsg('Ошибка WebSocket соединения');
        };

        ws.onclose = () => {
            disposeInput.dispose();
            if (connStatus === 'connected') {
                term.writeln('\r\n\x1b[33mСоединение закрыто\x1b[0m');
            }
            setConnStatus('idle');
        };
    }, [id, connStatus]);

    /* ── Отключение ── */
    const disconnect = () => {
        wsRef.current?.close();
        wsRef.current = null;
        setConnStatus('idle');
    };

    /* ── Сохранение SSH-реквизитов ── */
    const saveSSH = async () => {
        if (!id) return;
        setSaving(true);
        setFormError('');
        try {
            const res = await api.put<SSHConfig>(`/servers/${id}/ssh`, formData);
            setSshConfig(res);
            setShowForm(false);
        } catch (e: any) {
            setFormError(e.message);
        } finally {
            setSaving(false);
        }
    };

    /* ── Удаление SSH-реквизитов ── */
    const deleteSSH = async () => {
        if (!id || !window.confirm('Удалить SSH-реквизиты?')) return;
        try {
            await api.del(`/servers/${id}/ssh`);
            setSshConfig(null);
            disconnect();
        } catch (e: any) {
            alert(e.message);
        }
    };

    /* ── Открытие формы с предзаполнением ── */
    const openForm = () => {
        if (sshConfig && sshConfig !== 'loading') {
            setFormData({
                host_override: sshConfig.host_override || '',
                port: sshConfig.port,
                username: sshConfig.username,
                auth_method: sshConfig.auth_method,
                password: '', private_key: '', passphrase: '',
            });
        }
        setShowForm(true);
        setFormError('');
    };

    return (
        <div className="app-layout">
            <Sidebar />

            <main className="main-content terminal-main">
                {/* ── Header ── */}
                <header className="page-header" style={{ marginBottom: 16 }}>
                    <div>
                        <button className="btn-back" onClick={() => navigate(id ? `/servers/${id}` : '/servers')}>
                            ← Назад
                        </button>
                        <h1 style={{ marginTop: 8 }}>
                            Терминал {server ? `— ${server.name}` : ''}
                        </h1>
                    </div>
                    <div className="header-actions">
                        <span className={`ws-indicator ${connStatus === 'connected' ? 'ok' : connStatus === 'connecting' ? 'connecting' : 'error'}`}>
                            {connStatus === 'connected' ? '● подключён' : connStatus === 'connecting' ? '● подключение...' : '● отключён'}
                        </span>
                        {sshConfig && sshConfig !== 'loading' && connStatus === 'idle' && (
                            <button className="btn-primary" style={{ maxWidth: 160 }} onClick={connect}>
                                Подключиться
                            </button>
                        )}
                        {connStatus === 'connected' && (
                            <button className="btn-secondary btn-danger-outline" onClick={disconnect}>
                                Отключить
                            </button>
                        )}
                        <button className="btn-secondary" onClick={openForm}>
                            {sshConfig && sshConfig !== 'loading' ? 'Изменить SSH' : 'Настроить SSH'}
                        </button>
                        {sshConfig && sshConfig !== 'loading' && (
                            <button className="btn-secondary btn-danger-outline" onClick={deleteSSH}>
                                Удалить SSH
                            </button>
                        )}
                    </div>
                </header>

                {statusMsg && <div className="error-message" style={{ marginBottom: 12 }}>{statusMsg}</div>}

                {/* ── SSH не настроен ── */}
                {sshConfig === null && !showForm && (
                    <div className="agent-notice">
                        <span>🔑</span>
                        <div>
                            <strong>SSH-реквизиты не настроены</strong>
                            <p>Нажмите «Настроить SSH», чтобы указать данные для подключения.</p>
                        </div>
                    </div>
                )}

                {/* ── Форма SSH ── */}
                {showForm && (
                    <div className="settings-card" style={{ marginBottom: 16 }}>
                        <div className="settings-card-header">
                            <h2>🔑 SSH-реквизиты</h2>
                        </div>
                        {formError && <div className="error-message">{formError}</div>}

                        <div className="form-row">
                            <div className="form-group">
                                <label>Хост (если отличается)</label>
                                <input type="text" value={formData.host_override}
                                       onChange={e => setFormData(p => ({ ...p, host_override: e.target.value }))}
                                       placeholder={server?.host ?? 'IP или домен'} />
                                <span className="field-hint">Оставьте пустым — используется хост сервера</span>
                            </div>
                            <div className="form-group" style={{ maxWidth: 120 }}>
                                <label>Порт</label>
                                <input type="number" value={formData.port}
                                       onChange={e => setFormData(p => ({ ...p, port: Number(e.target.value) }))} />
                            </div>
                            <div className="form-group">
                                <label>Пользователь</label>
                                <input type="text" value={formData.username}
                                       onChange={e => setFormData(p => ({ ...p, username: e.target.value }))} />
                            </div>
                        </div>

                        <div className="form-row" style={{ marginBottom: 16 }}>
                            <label className="toggle">
                                <input type="radio" name="auth" checked={formData.auth_method === 'password'}
                                       onChange={() => setFormData(p => ({ ...p, auth_method: 'password' }))} />
                                <span>Пароль</span>
                            </label>
                            <label className="toggle">
                                <input type="radio" name="auth" checked={formData.auth_method === 'key'}
                                       onChange={() => setFormData(p => ({ ...p, auth_method: 'key' }))} />
                                <span>SSH-ключ</span>
                            </label>
                        </div>

                        {formData.auth_method === 'password' ? (
                            <div className="form-group">
                                <label>Пароль</label>
                                <input type="password" value={formData.password}
                                       onChange={e => setFormData(p => ({ ...p, password: e.target.value }))}
                                       placeholder="Введите пароль" />
                            </div>
                        ) : (
                            <>
                                <div className="form-group">
                                    <label>Приватный ключ</label>
                                    <textarea rows={5} value={formData.private_key}
                                              onChange={e => setFormData(p => ({ ...p, private_key: e.target.value }))}
                                              placeholder="-----BEGIN RSA PRIVATE KEY-----&#10;..."
                                              style={{ fontFamily: 'monospace', fontSize: 12 }} />
                                </div>
                                <div className="form-group">
                                    <label>Пароль ключа (если есть)</label>
                                    <input type="password" value={formData.passphrase}
                                           onChange={e => setFormData(p => ({ ...p, passphrase: e.target.value }))} />
                                </div>
                            </>
                        )}

                        <div className="modal-footer" style={{ padding: 0, marginTop: 16 }}>
                            <button className="btn-secondary" onClick={() => setShowForm(false)}>Отмена</button>
                            <button className="btn-primary" onClick={saveSSH} disabled={saving}>
                                {saving ? 'Сохранение...' : 'Сохранить'}
                            </button>
                        </div>
                    </div>
                )}

                {/* ── Терминал ── */}
                <div className="terminal-container" ref={containerRef} />
            </main>
        </div>
    );
} 
